import type { IntlShape } from 'react-intl';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import { ensurePdfFilename } from '../../../../data/projects/artifacts/ensurePdfFilename';

import { FormFillCard, type FilledField } from '../FormFillCard';
import type { OpenExportDocumentPayload } from './shared/exportDocumentPreview';
import { readRecordArray, readString, readToolData } from './shared/readers';
import { toArtefactStatus } from './status';

const readFields = (data: Record<string, unknown> | undefined): FilledField[] => {
  const raw = readRecordArray(data, 'fields');
  if (!raw) return [];
  return raw
    .map((row) => {
      const value = row.value;
      const stringValue =
        typeof value === 'string'
          ? value
          : typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : value == null
              ? ''
              : '';
      return {
        name: readString(row, 'name') ?? '',
        label: readString(row, 'label') ?? readString(row, 'name') ?? '',
        value: stringValue,
        page: typeof row.page === 'number' ? row.page : undefined,
      };
    })
    .filter((field) => field.label && field.value);
};

/**
 * Rich renderer for PDF form-fill tools. Animated progress, field summary,
 * and canvas preview on success. Failures fall through to the generic tool row.
 */
export const renderFillPdfFormToolUse = (
  item: ToolUseItem,
  intl: IntlShape,
  onOpenDocumentExport?: (payload: OpenExportDocumentPayload) => void
): JSX.Element | null => {
  const status = toArtefactStatus(item.status);
  const data = readToolData(item);
  const title =
    readString(data, 'title') ??
    readString(item.input, 'title') ??
    readString(data, 'filename') ??
    intl.formatMessage({ id: 'baRFiFaNBT', defaultMessage: 'Form' });

  if (status === 'generating') {
    return <FormFillCard status="generating" title={title} fields={[]} />;
  }

  if (status === 'completed') {
    const url = readString(data, 'download_url');
    if (!url) {
      return null;
    }
    const filename = ensurePdfFilename(readString(data, 'filename'));
    return (
      <FormFillCard
        status="completed"
        title={title}
        fields={readFields(data)}
        downloadUrl={url}
        onPreview={
          onOpenDocumentExport
            ? () =>
                onOpenDocumentExport({
                  url,
                  filename,
                  title,
                  forcePdfExtension: true,
                })
            : undefined
        }
      />
    );
  }

  return null;
};
